<template>
        <div class="language-selector-wrapper" ref="wrapper">
                <button
                        class="language-selector-button"
                        @click="toggleSelector"
                        :aria-expanded="listVisible.toString()"
                        aria-haspopup="listbox"
            :aria-label="'Change language. Current: ' + currentLocaleLabel"
                        type="button"
                        @keydown.down.prevent="openAndFocus(0)"
                        @keydown.enter.prevent="toggleSelector"
                        @keydown.space.prevent="toggleSelector"
                >
                        <span class="language-selector-icon" aria-hidden="true">🌐</span>
                        <span class="language-selector-label">{{ currentLocaleLabel }}</span>
                        <svg class="language-selector-chevron" :class="{ open: listVisible }" width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                <path d="M5.23 7.21a.75.75 0 011.06.02L10 10.939l3.71-3.71a.75.75 0 111.06 1.061l-4.24 4.24a.75.75 0 01-1.06 0L5.25 8.29a.75.75 0 01-.02-1.06z" fill="currentColor" />
                        </svg>
                </button>
                <transition name="language-fade-slide">
                    <ul
                        v-if="listVisible"
                        class="language-selector-list"
                        role="listbox"
                        :aria-activedescendant="activeDescendantId"
                        ref="list"
                        @keydown.down.prevent="moveFocus(1)"
                        @keydown.up.prevent="moveFocus(-1)"
                        @keydown.esc.prevent="closeSelector()"
                        @keydown.enter.prevent="selectFocused()"
                        @keydown.space.prevent="selectFocused()"
                        @keydown.tab="closeSelector()"
                    >
                        <li
                            v-for="(locale, idx) in locales"
                            :key="locale"
                            class="language-selector-locale"
                            role="option"
                            :id="'locale-option-' + locale"
                            :aria-selected="locale === ($i18n.locale || '').toLowerCase()"
                            :data-locale="locale"
                            :tabindex="focusedIndex === idx ? 0 : -1"
                            @click="changeLanguage($event, idx)"
                            @keydown.enter.prevent="changeLanguage($event, idx)"
                            @keydown.space.prevent="changeLanguage($event, idx)"
                            @focus="focusedIndex = idx"
                        >
                            {{ localeLabelMap[locale] }}
                        </li>
                    </ul>
                </transition>
        </div>
</template>

<script>
import i18n, { applyI18nLocale } from '@/plugins/i18n';
import { setLanguage } from '@/utils/languageDetection';

export default {
    name: 'LanguageSelector',
    data() {
        return {
            // Use lowercase external codes
            locales: ['id', 'en', 'ar'],
            listVisible: false,
            focusedIndex: -1,
            localeLabelMap: {
                en: 'English',
                id: 'Indonesia',
                ar: 'العربية'
            }
        }
    },
    computed: {
        currentLocaleLabel() {
            // i18n.locale internally is uppercase; map to lowercase key
            const lower = i18n.locale ? i18n.locale.toLowerCase() : 'en';
            return this.localeLabelMap[lower] || lower;
        },
        activeDescendantId() {
            if (this.focusedIndex < 0) return null;
            return 'locale-option-' + this.locales[this.focusedIndex];
        }
    },
    methods: {
        toggleSelector(e) {
            if (e) e.preventDefault();
            this.listVisible = !this.listVisible;
            if (this.listVisible) {
                this.$nextTick(() => {
                    const currentLower = i18n.locale ? i18n.locale.toLowerCase() : 'en';
                    this.focusedIndex = this.locales.indexOf(currentLower);
                    if (this.focusedIndex < 0) this.focusedIndex = 0;
                    const listEl = this.$refs.list;
                    if (listEl) listEl.focus({ preventScroll: true });
                });
            } else {
                this.focusedIndex = -1;
            }
        },
        closeSelector() {
            this.listVisible = false;
            this.focusedIndex = -1;
        },
        openAndFocus(index) {
            if (!this.listVisible) {
                this.toggleSelector();
            }
            this.$nextTick(() => {
                this.focusedIndex = index;
                this.focusLocaleElement();
            });
        },
        moveFocus(delta) {
            if (!this.listVisible) return;
            let next = this.focusedIndex + delta;
            if (next < 0) next = this.locales.length - 1;
            if (next >= this.locales.length) next = 0;
            this.focusedIndex = next;
            this.focusLocaleElement();
        },
        focusLocaleElement() {
            this.$nextTick(() => {
                const locale = this.locales[this.focusedIndex];
                if (!locale) return;
                const el = document.getElementById('locale-option-' + locale);
                if (el) el.focus({ preventScroll: true });
            });
        },
        selectFocused() {
            if (this.focusedIndex >= 0) {
                const locale = this.locales[this.focusedIndex];
                this.applyLocale(locale);
            }
        },
        changeLanguage(e, idx) {
            if (e) e.preventDefault();
            let locale;
            if (e && e.target && e.target.dataset && e.target.dataset.locale) {
                locale = e.target.dataset.locale;
            } else {
                locale = this.locales[idx];
            }
            this.applyLocale(locale);
        },
        applyLocale(locale) {
            if (!locale) return this.closeSelector();
            const lower = locale.toLowerCase();
            const currentLower = (i18n.locale || 'EN').toLowerCase();
            if (lower === currentLower) {
                this.closeSelector();
                return;
            }
            // Persist user override & apply
            setLanguage(lower, (l) => {
                applyI18nLocale(l);
            });
            this.closeSelector();
        },
        handleClickOutside(e) {
            const wrapper = this.$refs.wrapper;
            if (wrapper && !wrapper.contains(e.target)) {
                this.closeSelector();
            }
        }
    },
    mounted() {
        document.addEventListener('click', this.handleClickOutside);
    },
    beforeDestroy() { // Vue 2 lifecycle name
        document.removeEventListener('click', this.handleClickOutside);
    }
}
</script>

<style scoped>
.language-selector-wrapper {
    position: relative;
    display: inline-flex;
}

.language-selector-button {
    margin: 0;
    padding: 0.4rem 0.85rem 0.4rem 0.75rem;
    border: 1px solid rgba(255,255,255,0.9);
    background: rgba(255,255,255,0.08);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    display: inline-flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    column-gap: 0.5rem;
    color: #fff;
    font-weight: 600;
    border-radius: 9999px;
    cursor: pointer;
    line-height: 1.1;
    font-size: 0.9rem;
    letter-spacing: .5px;
    transition: background .25s ease, border-color .25s ease, box-shadow .25s ease, transform .15s ease;
}

 .language-selector-icon { font-size: 1rem; line-height: 1; }
 .language-selector-label { white-space: nowrap; }
 .language-selector-chevron { transition: transform .25s ease; }
 .language-selector-chevron.open { transform: rotate(180deg); }

 .language-selector-button:hover {
    background: rgba(255,255,255,0.18);
    box-shadow: 0 0 0 3px rgba(255,255,255,0.15);
 }
 .language-selector-button:active {
    transform: scale(.96);
    background: rgba(255,255,255,0.24);
 }
 .language-selector-button:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(255,255,255,0.35);
 }

.language-selector-list {
    min-width: 10rem;
    position: absolute;
    top: calc(100% + .5rem);
    right: 0;
    background: rgba(10,10,10,0.85);
    border: 1px solid rgba(255,255,255,0.15);
    display: flex;
    flex-direction: column;
    border-radius: 0.75rem;
    padding: 0.35rem;
    backdrop-filter: blur(8px) saturate(140%);
    -webkit-backdrop-filter: blur(8px) saturate(140%);
    box-shadow: 0 8px 28px -4px rgba(0,0,0,0.55), 0 2px 6px -1px rgba(0,0,0,0.25);
    outline: none;
    list-style: none;
    margin: 0;
    z-index: 1000;
}

.language-selector-list:dir(rtl) {
    left: 0;
    right: unset;
}

 .language-selector-locale {
    width: 100%;
    text-decoration: none;
    color: #fff;
    font-weight: 500;
    padding: 0.55rem 0.75rem;
    border-radius: 0.55rem;
    cursor: pointer;
    outline: none;
    font-size: 0.85rem;
    letter-spacing: .3px;
    transition: background .2s ease, color .2s ease, transform .15s ease;
 }
 .language-selector-locale[aria-selected="true"] {
    background: linear-gradient(135deg, rgba(255,255,255,0.25), rgba(255,255,255,0.1));
    color: #fff;
 }
 .language-selector-locale:hover,
 .language-selector-locale:focus-visible {
    background: rgba(255,255,255,0.18);
 }
 .language-selector-locale:active {
    transform: scale(.97);
 }

 /* Transition for dropdown */
 .language-fade-slide-enter-active { transition: opacity .22s ease, transform .24s cubic-bezier(.4,.1,.2,1); }
 .language-fade-slide-leave-active { transition: opacity .18s ease, transform .18s cubic-bezier(.4,.1,.2,1); }
 .language-fade-slide-enter, .language-fade-slide-leave-to { opacity: 0; transform: translateY(-4px) scale(.98); }
 
 /* Accessibility helper for visually hiding while keeping available to screen readers */
 .visually-hidden { position:absolute !important; width:1px !important; height:1px !important; padding:0 !important; margin:-1px !important; overflow:hidden !important; clip:rect(0 0 0 0) !important; white-space:nowrap !important; border:0 !important; }
 
 /* Tablet & small desktop adjustments */
 @media (max-width: 992px) {
     .language-selector-button {
         padding: 0.38rem 0.7rem 0.38rem 0.6rem;
         font-size: 0.8rem;
         column-gap: 0.4rem;
     }
     /* Allow wider label at this breakpoint */
     .language-selector-label { max-width: 100%; }
 }
 
 /* Phones */
 @media (max-width: 575.98px) {
     .language-selector-button {
        padding: 0.30rem 0.50rem 0.30rem 0.45rem; /* tighter horizontal padding */
        font-size: 0.7rem;
        column-gap: 0.18rem; /* reduce space between label and chevron */
         border-radius: 20px;
     }
     /* Hide globe icon on phones to save horizontal space */
     .language-selector-icon { display: none; }
     /* Always show full language name on phones */
     .language-selector-label { max-width: unset; overflow: visible; text-overflow: unset; }
     /* Slightly shrink chevron and pull it closer */
     .language-selector-chevron { width: 12px; height: 12px; margin-left: 0.05rem; }
 }
 
 /* Extra small devices: tighten padding slightly but keep label visible */
 @media (max-width: 400px) {
     .language-selector-button { padding: 0.28rem 0.45rem 0.28rem 0.4rem; font-size: 0.66rem; column-gap: 0.15rem; }
     .language-selector-chevron { width: 11px; height: 11px; margin-left: 0.04rem; }
 }
 
 @media (prefers-reduced-motion: reduce) {
    .language-selector-button, .language-selector-locale, .language-fade-slide-enter-active, .language-fade-slide-leave-active { transition: none !important; }
 }
</style>